import { Head, useMutation } from 'arcway/lib/client'

import { Container } from '#components/container.jsx'
import { PublicLayout } from '#components/layouts/public.jsx'
import { Button } from '#components/ui/button.jsx'
import { Card, CardContent } from '#components/ui/card.jsx'
import { ErrorAlert } from '#components/ui/error-alert.jsx'
import { Field, FieldGroup, FieldLabel } from '#components/ui/field.jsx'
import { Input } from '#components/ui/input.jsx'

export default function EnterprisePage() {
  const enquiry = useMutation('/contact/enterprise', { method: 'POST' })

  async function submit(event) {
    event.preventDefault()
    if (enquiry.loading) return
    const data = new FormData(event.currentTarget)

    try {
      await enquiry.trigger({
        name: data.get('name'),
        email: data.get('email'),
        organization: data.get('organization') || undefined,
        phone: data.get('phone') || undefined,
        requirements: data.get('requirements') || undefined,
      })
    } catch {}
  }

  return <EnterpriseContent enquiry={enquiry} onSubmit={submit} />
}

export function EnterpriseContent({ enquiry, onSubmit }) {
  return (
    <>
      <Head
        description='Tell Mono about your podcast hosting requirements.'
        title='Enterprise podcast hosting — mono.fm'
      />
      <PublicLayout editorial>
        <div>
          <Container className='enterprise-wrap'>
            <div className='enterprise-grid'>
              <header className='enterprise-intro'>
                <p className='guide-eyebrow'>Enterprise</p>
                <h1>Tell us what you need</h1>
                <p className='guide-intro'>
                  Share your contact details and any optional requirements. We
                  will review them and contact you directly.
                </p>
                <div className='enterprise-notes'>
                  <h2>A conversation about your podcast</h2>
                  <p>
                    Tell us about the shows you host, your team, and what you
                    need from Mono. If you are moving an existing podcast,
                    include any migration requirements.
                  </p>
                  <a href='/docs'>Explore the hosting guides →</a>
                </div>
              </header>

              <Card className='enterprise-form-card'>
                <CardContent className='pt-6'>
                  {enquiry.data?.accepted ? (
                    <div aria-live='polite'>
                      <h2 className='text-xl font-semibold'>
                        Enquiry received
                      </h2>
                      <p className='mt-2 text-muted-foreground'>
                        Thank you. We will contact you after reviewing your
                        requirements.
                      </p>
                    </div>
                  ) : (
                    <form
                      className='space-y-6'
                      onSubmit={onSubmit}
                      aria-busy={enquiry.loading || undefined}
                    >
                      <FieldGroup>
                        <Field>
                          <FieldLabel htmlFor='enterprise-name'>
                            Name
                          </FieldLabel>
                          <Input
                            autoComplete='name'
                            id='enterprise-name'
                            maxLength='160'
                            name='name'
                            required
                          />
                        </Field>
                        <Field>
                          <FieldLabel htmlFor='enterprise-email'>
                            Email
                          </FieldLabel>
                          <Input
                            autoComplete='email'
                            id='enterprise-email'
                            maxLength='320'
                            name='email'
                            required
                            type='email'
                          />
                        </Field>
                        <Field>
                          <FieldLabel htmlFor='enterprise-organization'>
                            Organization{' '}
                            <span className='font-normal'>(optional)</span>
                          </FieldLabel>
                          <Input
                            autoComplete='organization'
                            id='enterprise-organization'
                            maxLength='160'
                            name='organization'
                          />
                        </Field>
                        <Field>
                          <FieldLabel htmlFor='enterprise-phone'>
                            Phone{' '}
                            <span className='font-normal'>(optional)</span>
                          </FieldLabel>
                          <Input
                            autoComplete='tel'
                            id='enterprise-phone'
                            maxLength='64'
                            name='phone'
                            type='tel'
                          />
                        </Field>
                        <Field>
                          <FieldLabel htmlFor='enterprise-requirements'>
                            Requirements{' '}
                            <span className='font-normal'>(optional)</span>
                          </FieldLabel>
                          <textarea
                            className='min-h-40 w-full rounded-md border bg-background px-3 py-2 text-sm'
                            id='enterprise-requirements'
                            maxLength='5000'
                            name='requirements'
                          />
                        </Field>
                      </FieldGroup>

                      {enquiry.error && (
                        <ErrorAlert>{enquiry.error.message}</ErrorAlert>
                      )}
                      {enquiry.loading && (
                        <p
                          role='status'
                          className='text-sm text-muted-foreground'
                        >
                          Sending your enquiry…
                        </p>
                      )}
                      <Button loading={enquiry.loading} type='submit'>
                        Send enquiry
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </Container>
        </div>
      </PublicLayout>
    </>
  )
}
